export type DevSettingSection = 'general' | 'recording' | 'tasks';

export interface IDevSettingMetadata {
    name: string;
    description: string;
    section: DevSettingSection;
}

// All dev setting keys in one place — adding a new setting starts here
export const DEV_SETTING_KEYS = [
    'useStaging',
    'simulateNative',
    'enableHexagonTools',
    'eligibleForTrial',
    'bypassPayments',
    'showVeryShortExpiration',
    'disableToastAutoDismiss',
    'showApptRecordingNotificationBtn',
    'recordingShortPauseAutoSave',
    'disableRecordingContextStart',
    'ignoreShortRecordingMessages',
    'disableRecordingProcessing',
    'disableDirectUploadAutoRetry',
    'simulateIndexedDBFailure',
    'simulateFilesystemFailure',
    'showTaskNotificationTestBtns',
    'simulateTasksStatus',
    'showSharingPrototype',
] as const;

export type DevSettingKey = typeof DEV_SETTING_KEYS[number];

export const DEV_SETTINGS_METADATA: Record<DevSettingKey, IDevSettingMetadata> = {
    useStaging: {
        name: 'Use Staging',
        description: 'Connect to staging API instead of production (requires app restart)',
        section: 'general'
    },
    simulateNative: {
        name: 'Simulate Native',
        description: 'Make web behave like native device for testing device-specific features',
        section: 'general'
    },
    enableHexagonTools: {
        name: 'Enable Hexagon Tools',
        description: 'Show hexagon manipulation tools on care map for layout debugging',
        section: 'general'
    },
    eligibleForTrial: {
        name: 'Eligible For Trial',
        description: 'Force trial eligibility state for testing payment flows',
        section: 'general'
    },
    bypassPayments: {
        name: 'Bypass Payments',
        description: 'Use bypass mode for payments instead of real payment processing',
        section: 'general'
    },
    showVeryShortExpiration: {
        name: 'Show Very Short Expiration',
        description: 'Use short expiration times (minutes) for testing expiry behavior',
        section: 'general'
    },
    disableToastAutoDismiss: {
        name: 'Disable Toast Auto Dismiss',
        description: 'Keep toast notifications visible until manually closed for inspection',
        section: 'general'
    },
    showSharingPrototype: {
        name: 'Show Sharing Prototype (IH-6912)',
        description: 'Open the throwaway Sharing & Access prototype instead of the real sheet, for visual comparison',
        section: 'general'
    },

    // Recording settings
    showApptRecordingNotificationBtn: {
        name: 'Show Appt Recording Notification Btn',
        description: 'Display a button to test appointment recording push notifications',
        section: 'recording'
    },
    recordingShortPauseAutoSave: {
        name: 'Recording Short Pause Auto Save',
        description: 'Use 5-second pause threshold instead of 60 for faster testing',
        section: 'recording'
    },
    disableRecordingContextStart: {
        name: 'Disable Recording Context Start',
        description: 'Prevent recording context from auto-starting for debugging',
        section: 'recording'
    },
    ignoreShortRecordingMessages: {
        name: 'Ignore Short Recording Messages',
        description: 'Suppress warnings about recordings being too short',
        section: 'recording'
    },
    disableRecordingProcessing: {
        name: 'Disable Recording Processing',
        description: 'Stop recordings from being sent to server for transcription',
        section: 'recording'
    },
    disableDirectUploadAutoRetry: {
        name: 'Disable Direct Upload Auto-Retry',
        description: 'Disables auto-retry when uploading recordings directly to S3',
        section: 'recording'
    },
    simulateIndexedDBFailure: {
        name: 'Simulate IndexedDB Failure',
        description: 'Force IndexedDB errors to test offline storage fallback behavior',
        section: 'recording'
    },
    simulateFilesystemFailure: {
        name: 'Simulate Filesystem Failure',
        description: 'Force filesystem errors to test native storage fallback behavior',
        section: 'recording'
    },

    // Task settings
    showTaskNotificationTestBtns: {
        name: 'Show Task Notification Test Btns',
        description: 'Display buttons to manually trigger task notification scenarios',
        section: 'tasks'
    },
    simulateTasksStatus: {
        name: 'Simulate Tasks Status',
        description: 'Simulate nighttime to test evening task states and notifications',
        section: 'tasks'
    }
};

export const SECTION_INFO: Record<DevSettingSection, { title: string; order: number }> = {
    general: { title: 'Dev Settings', order: 1 },
    recording: { title: 'Recording Dev Settings', order: 2 },
    tasks: { title: 'Task Dev Settings', order: 3 }
};

export function getSettingsBySection(section: DevSettingSection): DevSettingKey[] {
    return (Object.keys(DEV_SETTINGS_METADATA) as DevSettingKey[])
        .filter(key => DEV_SETTINGS_METADATA[key].section === section);
}
